import * as React from "react";

import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import NewChatButton from "./NewChatButton";
import { Avatar } from "./ThreadMessageView";

const GREEN = "#3CE97C";

interface ChatHeaderProps {
    /**
     * What the chat says it is doing. "Picked for you" is the promise a
     * home-screen prompt already made; opened from the launcher nothing has
     * been picked yet, and claiming otherwise over an empty thread is a lie
     * the user can see.
     */
    status: string;
    /** Whether there is a conversation worth clearing. */
    canClear: boolean;
    /** No clearing mid-turn: the answer would land in the cleared thread. */
    busy: boolean;
    onNewChat: () => void;
    onClose: () => void;
}

/**
 * The chat's own title bar.
 *
 * Its own component because the overlay renders a fullscreen surface and so
 * paints this itself, and because everything in it is presentation — which
 * kept the one function that actually runs the conversation from being read in
 * one sitting.
 */
export default function ChatHeader({
    status,
    canClear,
    busy,
    onNewChat,
    onClose,
}: ChatHeaderProps): React.JSX.Element {
    return (
        <Stack
            direction="row"
            sx={{
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.5,
                flexShrink: 0,
                borderBottom: "1px solid rgba(168,184,217,0.10)",
            }}
        >
            <Avatar size={40} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.1 }}
                >
                    Volley
                </Typography>
                <Stack direction="row" sx={{ alignItems: "center", gap: 0.75 }}>
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: GREEN,
                            boxShadow: `0 0 6px ${GREEN}`,
                        }}
                    />
                    <Typography
                        sx={{ fontSize: 12, color: GREEN, fontWeight: 500 }}
                    >
                        {status}
                    </Typography>
                </Stack>
            </Box>
            {/* Only once there is something to clear: on a fresh thread it
                would be an action with nothing to act on. */}
            {canClear && <NewChatButton disabled={busy} onClick={onNewChat} />}
            <IconButton
                onClick={onClose}
                aria-label="close"
                sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.06)" }}
            >
                <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
        </Stack>
    );
}
